import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { OrgChartService, UserOrgRoleResponse, MyTeamResponse, MySuperiorResponse } from '@/api/orgChart';

interface OrgChartContextType {
  userRole: UserOrgRoleResponse | null;
  myTeam: MyTeamResponse;
  mySuperior: MySuperiorResponse;
  isLoading: boolean;
  refetch: () => void;
}

const OrgChartContext = createContext<OrgChartContextType | undefined>(undefined);

export const OrgChartProvider: React.FC<{ children: React.ReactNode; tenantId?: number }> = ({
  children,
  tenantId,
}) => {
  const [userRole, setUserRole] = useState<UserOrgRoleResponse | null>(null);
  const [myTeam, setMyTeam] = useState<MyTeamResponse>([]);
  const [mySuperior, setMySuperior] = useState<MySuperiorResponse>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (!tenantId) {
      setIsLoading(false);
      setUserRole(null);
      setMyTeam([]);
      setMySuperior([]);
      return;
    }

    setIsLoading(true);
    try {
      // Parallel fetch for efficiency
      const [roleData, teamData, superiorData] = await Promise.all([
        OrgChartService.getMyOrgRole(tenantId),
        OrgChartService.getMyTeam(tenantId),
        OrgChartService.getMySuperior(tenantId),
      ]);

      setUserRole(roleData);
      setMyTeam(teamData);
      setMySuperior(superiorData);
    } catch (error) {
      console.error('Failed to fetch org chart data', error);
    } finally {
      setIsLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const value = useMemo(
    () => ({
      userRole,
      myTeam,
      mySuperior,
      isLoading,
      refetch: fetchData,
    }),
    [userRole, myTeam, mySuperior, isLoading, fetchData],
  );

  return <OrgChartContext.Provider value={value}>{children}</OrgChartContext.Provider>;
};

export const useOrgChartContext = () => {
  const context = useContext(OrgChartContext);
  if (context === undefined) {
    throw new Error('useOrgChartContext must be used within an OrgChartProvider');
  }
  return context;
};
